import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Sparkles, Send, ArrowDown, X } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { Message } from "../types";
import { GuestLoginModal } from "./GuestLoginModal";

interface AIVibeCheckModalProps {
  isOpen: boolean;
  onClose: () => void;
  apartmentTitle: string;
  apartmentId: string;
  messages: Message[];
  onSendMessage: (message: string) => void;
  isLoading: boolean;
  onLoadHistory?: (apartmentId: string) => void | Promise<void>;
  isGuest?: boolean; // Show disclaimer for guest users
}

// Helper function to extract brokerId from URL
const getBrokerIdFromUrl = (): string | null => {
  const hash = window.location.hash;
  if (hash) {
    const match = hash.match(/\/stories\/([^/?]+)/);
    return match ? match[1] : null;
  }
  const match = window.location.pathname.match(/\/stories\/([^/?]+)/);
  return match ? match[1] : null;
};

export function AIVibeCheckModal({
  isOpen,
  onClose,
  apartmentTitle,
  apartmentId,
  messages,
  onSendMessage,
  isLoading,
  onLoadHistory,
  isGuest = false,
}: AIVibeCheckModalProps) {
  const { t } = useTranslation();
  const [input, setInput] = useState("");
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isUserAtBottom, setIsUserAtBottom] = useState(true);
  const [viewportHeight, setViewportHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 0
  );
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [viewportOffset, setViewportOffset] = useState({ top: 0, left: 0 });
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Refs for scroll management
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load conversation history when modal opens
  useEffect(() => {
    if (isOpen && onLoadHistory && !isLoadingHistory) {
      setIsLoadingHistory(true);
      const loadHistory = async () => {
        try {
          await onLoadHistory(apartmentId);
        } catch (error) {
          console.error("Error loading conversation history:", error);
        } finally {
          setIsLoadingHistory(false);
        }
      };
      loadHistory();
    }
  }, [isOpen, apartmentId]);

  // Scroll to bottom function
  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior, block: 'end' });
  };

  // Check if user is at bottom of messages
  const checkIfAtBottom = () => {
    if (!messagesContainerRef.current) return true;
    
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const isAtBottom = distanceFromBottom < 100; // Within 100px of bottom
    
    setIsUserAtBottom(isAtBottom);
    setShowScrollButton(!isAtBottom && messages.length > 0);
    
    return isAtBottom;
  };

  // Handle scroll event
  const handleScroll = () => {
    checkIfAtBottom();
  };

  // Auto-scroll when messages change (only if user is at bottom)
  useLayoutEffect(() => {
    if (isOpen && messages.length > 0) {
      // Small delay to ensure DOM has updated
      const timeoutId = setTimeout(() => {
        if (isUserAtBottom) {
          scrollToBottom('smooth');
        }
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [messages, isOpen, isUserAtBottom]);

  // Scroll to bottom when modal opens or history finishes loading
  useEffect(() => {
    if (isOpen && !isLoadingHistory) {
      // Immediate scroll when opening or history loaded
      setTimeout(() => scrollToBottom('auto'), 150);
    }
  }, [isOpen, isLoadingHistory]);

  // Auto-scroll when loading finishes (AI response received)
  useEffect(() => {
    if (!isLoading && messages.length > 0) {
      setTimeout(() => scrollToBottom('smooth'), 100);
    }
  }, [isLoading]);

  // Handle mobile keyboard with visualViewport API
  useEffect(() => {
    if (!isOpen) return;
    
    if (typeof window === 'undefined' || !window.visualViewport) {
      // Fallback: use window.innerHeight for browsers without visualViewport
      const handleResize = () => {
        setViewportHeight(window.innerHeight);
        setViewportOffset({ top: 0, left: 0 });
      };
      
      setViewportHeight(window.innerHeight);
      setViewportOffset({ top: 0, left: 0 });
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }

    const handleViewportResize = () => {
      try {
        const newHeight = window.visualViewport!.height;
        const newOffsetTop = window.visualViewport!.offsetTop;
        const newOffsetLeft = window.visualViewport!.offsetLeft;
        
        setViewportHeight(newHeight);
        setViewportOffset({ top: newOffsetTop, left: newOffsetLeft });
        
        // Detect keyboard state
        const isKeyboardNowOpen = newHeight < window.innerHeight - 150;
        if (isKeyboardNowOpen !== isKeyboardOpen) {
          setIsKeyboardOpen(isKeyboardNowOpen);
        }
        
        // Only scroll if user was already at bottom
        requestAnimationFrame(() => {
          if (isUserAtBottom) {
            setTimeout(() => scrollToBottom('auto'), 150);
          }
        });
      } catch (error) {
        console.error('[AIVibeCheck] Viewport resize error:', error);
      }
    };

    // Set initial height and offset
    const initialHeight = window.visualViewport.height;
    const initialOffsetTop = window.visualViewport.offsetTop;
    const initialOffsetLeft = window.visualViewport.offsetLeft;
    
    setViewportHeight(initialHeight);
    setViewportOffset({ top: initialOffsetTop, left: initialOffsetLeft });
    
    window.visualViewport.addEventListener('resize', handleViewportResize);
    window.visualViewport.addEventListener('scroll', handleViewportResize);

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportResize);
        window.visualViewport.removeEventListener('scroll', handleViewportResize);
      }
    };
  }, [isOpen]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  // Handle input focus to ensure visibility
  const handleInputFocus = () => {
    // Mark user at bottom for auto-scroll behavior
    setIsUserAtBottom(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;
    
    onSendMessage(input);
    setInput("");
    
    // Ensure we scroll to bottom after sending
    setIsUserAtBottom(true);
    setTimeout(() => scrollToBottom('smooth'), 100);
  };

  // Return null if not open (controlled by parent)
  if (!isOpen) {
    return null;
  }

  return (
    <DialogPrimitive.Root open={true} onOpenChange={(open: boolean) => !open && onClose()}>
      <DialogPrimitive.Portal>
        {/* Overlay - simple backdrop without animations */}
        <DialogPrimitive.Overlay 
          className="fixed inset-0 z-50 bg-black/50"
          style={{ animation: 'none' }}
        />
        {/* Content - Explicit positioning for keyboard handling */}
        <DialogPrimitive.Content
          role="dialog"
          className="fixed z-50 bg-white md:rounded-3xl"
          style={{
            top: typeof window !== 'undefined' && window.innerWidth < 768 
              ? viewportOffset.top 
              : 0,
            left: typeof window !== 'undefined' && window.innerWidth < 768 
              ? viewportOffset.left 
              : 0,
            right: typeof window !== 'undefined' && window.innerWidth < 768 
              ? viewportOffset.left 
              : 0,
            height: typeof window !== 'undefined' && window.visualViewport 
              ? `${viewportHeight}px` 
              : '100dvh',
            maxHeight: typeof window !== 'undefined' && window.visualViewport 
              ? `${viewportHeight}px` 
              : '100dvh',
            overflow: 'hidden',
          }}
        >
          {/* Close button */}
          <DialogPrimitive.Close 
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/80 hover:bg-white shadow-md transition-colors"
            style={{ marginTop: 'max(0px, env(safe-area-inset-top))' }}
      >
            <X className="w-5 h-5 text-gray-600" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>

        {/* Hidden accessibility components - required by Dialog */}
          <DialogPrimitive.Title className="sr-only">{t('vibeCheck.title')}</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
          {apartmentTitle}
          </DialogPrimitive.Description>

        {/* Flex container wrapper to override Dialog's grid layout */}
        <div 
          className="flex flex-col w-full relative"
          style={{
            height: '100%',
            zIndex: 0
          }}
        >
          {/* Header */}
          <div 
            className="flex-shrink-0 p-4 md:p-6 pb-3 md:pb-4 border-b bg-white md:rounded-t-3xl"
            style={{ 
              paddingTop: 'max(1rem, env(safe-area-inset-top))'
            }}
          >
            <div className="flex items-center gap-3 pr-12">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 text-lg font-bold leading-tight">{t('vibeCheck.title')}</h2>
                <p className="text-sm text-gray-600 mt-0.5 line-clamp-1">{apartmentTitle}</p>
              </div>
            </div>
          </div>

          {/* Guest Disclaimer Banner */}
          {isGuest && (
            <motion.div 
              initial={{ y: -20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="flex-shrink-0 bg-gradient-to-r from-purple-50 via-pink-50 to-purple-50 border-b border-purple-200 px-4 py-3"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 flex-1">
                  <div className="flex-1">
                    <p className="text-sm font-medium bg-gradient-to-r from-purple-700 via-pink-600 to-purple-700 bg-clip-text text-transparent">
                      {t('guest.vibeCheckDisclaimerTitle')}
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => {
                    // Store vibeCheck state before opening login modal (for guest users in story mode)
                    if (isGuest) {
                      const brokerId = getBrokerIdFromUrl();
                      if (brokerId) {
                        localStorage.setItem('pending_vibecheck_state', JSON.stringify({
                          brokerId,
                          apartmentId,
                          apartmentTitle,
                          messages,
                        }));
                      }
                    }
                    setShowLoginModal(true);
                  }}
                  className="bg-purple-600 hover:bg-purple-700 text-white text-xs px-3 py-1.5 h-auto rounded-full shadow-sm flex-shrink-0 font-medium"
                >
                  {t('guest.loginButton')}
                </Button>
              </div>
            </motion.div>
          )}

          {/* Messages Container - Takes all available space */}
          <div 
            ref={messagesContainerRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4 bg-gray-50"
            style={{
              minHeight: 0,
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
              touchAction: 'pan-y',
            }}
          >
          {/* Loading History Skeleton */}
          {isLoadingHistory && messages.length === 0 && (
            <div className="space-y-4 animate-pulse">
              <div className="flex justify-end">
                <div className="max-w-[80%] h-16 bg-gray-200 rounded-2xl"></div>
              </div>
              <div className="flex justify-start">
                <div className="max-w-[80%] h-20 bg-gray-200 rounded-2xl"></div>
              </div>
              <div className="flex justify-end">
                <div className="max-w-[80%] h-12 bg-gray-200 rounded-2xl"></div>
              </div>
            </div>
          )}

          {/* Empty State */}
          {messages.length === 0 && !isLoadingHistory && (
            <div className="text-center py-8 h-full flex flex-col items-center justify-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-purple-600" />
              </div>
              <p className="text-gray-600 mb-2 font-medium">{t('vibeCheck.askAnything')}</p>
              <p className="text-sm text-gray-500 px-4">
                {t('vibeCheck.tryAsking')}
              </p>
            </div>
          )}

          {/* Messages */}
          <AnimatePresence>
            {messages.map((message) => (
              <motion.div
                key={message.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${
                  message.role === "user" ? "justify-end" : "justify-start"
                }`}
              >
                <div
                  className={`max-w-[85%] md:max-w-[80%] rounded-2xl px-4 py-3 shadow-sm ${
                    message.role === "user"
                      ? "bg-gradient-to-r from-blue-600 to-purple-600 text-white"
                      : "bg-white text-gray-900 border border-gray-100"
                  }`}
                >
                  <p className="text-sm leading-relaxed break-words">{message.content}</p>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {/* AI Typing Indicator */}
          {isLoading && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="flex justify-start"
            >
              <div className="bg-white border border-gray-100 rounded-2xl px-4 py-3 shadow-sm">
                <div className="flex gap-1">
                  <motion.div
                    className="w-2 h-2 bg-purple-400 rounded-full"
                    animate={{ y: [0, -5, 0] }}
                    transition={{ repeat: Infinity, duration: 0.6, delay: 0 }}
                  />
                  <motion.div
                    className="w-2 h-2 bg-purple-400 rounded-full"
                    animate={{ y: [0, -5, 0] }}
                    transition={{
                      repeat: Infinity,
                      duration: 0.6,
                      delay: 0.1,
                    }}
                  />
                  <motion.div
                    className="w-2 h-2 bg-purple-400 rounded-full"
                    animate={{ y: [0, -5, 0] }}
                    transition={{
                      repeat: Infinity,
                      duration: 0.6,
                      delay: 0.2,
                    }}
                  />
                </div>
              </div>
            </motion.div>
          )}

          {/* Scroll anchor */}
          <div ref={messagesEndRef} className="h-1" />

          {/* Floating Scroll to Bottom Button */}
          <AnimatePresence>
            {showScrollButton && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.8, y: 10 }}
                className="absolute bottom-4 right-4 z-10"
              >
                <Button
                  onClick={() => {
                    setIsUserAtBottom(true);
                    scrollToBottom('smooth');
                  }}
                  size="icon"
                  className="h-11 w-11 rounded-full bg-white border-2 border-purple-200 shadow-lg hover:bg-purple-50 hover:border-purple-300 transition-all"
                >
                  <ArrowDown className="w-5 h-5 text-purple-600" />
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

          {/* Input Form - Fixed at bottom */}
          <form
            onSubmit={handleSubmit}
            className="flex-shrink-0 p-4 border-t bg-white md:rounded-b-3xl"
            style={{ 
              paddingBottom: 'max(1rem, env(safe-area-inset-bottom))',
              paddingLeft: 'max(1rem, env(safe-area-inset-left))',
              paddingRight: 'max(1rem, env(safe-area-inset-right))'
            }}
          >
            <div className="flex gap-2 items-end">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onFocus={handleInputFocus}
                placeholder={t('vibeCheck.placeholder')}
                className="flex-1 h-12 md:h-11 rounded-xl text-base resize-none"
                disabled={isLoading}
                inputMode="text"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="sentences"
              />
              <Button
                type="submit"
                size="icon"
                disabled={isLoading || !input.trim()}
                className="h-12 w-12 md:h-11 md:w-11 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 shadow-lg disabled:opacity-50 flex-shrink-0"
              >
                <Send className="w-5 h-5 md:w-4 md:h-4" />
              </Button>
            </div>
          </form>
        </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
      
      {/* Guest Login Modal */}
      {isGuest && (
        <GuestLoginModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          onSuccess={() => {
            // After successful login, the auth state change will be handled
            // The parent component should detect the auth change and update isGuest
            setShowLoginModal(false);
          }}
        />
      )}
    </DialogPrimitive.Root>
  );
}
